import { useCallback } from 'react';

import { monthlyinstalmentApi } from 'scenes/common/api';
import {
  isBusinessError,
  isApiError,
  makeApiReloading,
  makeApiSuccess,
} from 'scenes/common/helpers/apiData';
import { useFunnelState } from 'scenes/common/contexts/quoteFlowContext';
import {
  HomeFunnelState,
  HomeFormState,
  HomeStepType,
  HomeApiResponses,
  ReferenceData,
} from 'scenes/home/types';
import { quoteApi } from 'scenes/home/api';
import { InsuranceName } from 'scenes/common/types';
import {
  getPolicyStartDate,
  getQuoteNumber,
  getQuoteReference,
} from 'scenes/home/selectors';
import { handleError } from 'scenes/home/api/quote/services/quote';
import { HomeFormValues } from './types';

export const usePolicyActions = (
  onNextIfLocationIs: (s: HomeStepType, values?: HomeFormState) => void,
  onApiResponse: (data: Partial<HomeApiResponses>) => void,
  referenceData: ReferenceData
) => {
  const funnelState = useFunnelState<HomeFunnelState>();

  const handleQuoteResponse = useCallback(
    (quoteDetails) => {
      onApiResponse({
        quoteDetails,
      });
      return quoteDetails;
    },
    [onApiResponse]
  );

  const onUpdateToPolicyDetails = useCallback(
    (values: HomeFormValues) => {
      onApiResponse({
        quoteDetails: makeApiReloading(funnelState.apiResponses.quoteDetails),
      });
      quoteApi()
        .updateToPolicyDetails(funnelState, referenceData)
        .then(handleQuoteResponse)
        .then((quoteDetails) => {
          if (!isBusinessError(quoteDetails) && !isApiError(quoteDetails)) {
            onNextIfLocationIs(funnelState.step, values);
          }
        });
    },
    [
      funnelState,
      handleQuoteResponse,
      onApiResponse,
      onNextIfLocationIs,
      referenceData,
    ]
  );

  const onCompleteQuote = useCallback(() => {
    onApiResponse({
      quoteDetails: makeApiReloading(funnelState.apiResponses.quoteDetails),
    });
    quoteApi()
      .updateToDataComplete(funnelState, referenceData)
      .then((quoteDetails) => {
        if (!isBusinessError(quoteDetails) && !isApiError(quoteDetails)) {
          monthlyinstalmentApi()
            .recalculateMonthlyInstalment(
              InsuranceName.Home,
              getQuoteNumber(funnelState),
              getQuoteReference(funnelState),
              getPolicyStartDate(funnelState)
            )
            .then((recalculatedInstalments) =>
              makeApiSuccess({
                ...funnelState.apiResponses.quoteDetails.data,
                recalculatedInstalments,
              })
            )
            .then(handleQuoteResponse)
            .then((details) => {
              if (!isBusinessError(details) && !isApiError(details)) {
                onNextIfLocationIs(funnelState.step);
              }
            })
            .catch((err) =>
              handleQuoteResponse(handleError(err, funnelState, referenceData))
            );
        } else {
          handleQuoteResponse(quoteDetails);
        }
      });
  }, [
    onApiResponse,
    funnelState,
    referenceData,
    handleQuoteResponse,
    onNextIfLocationIs,
  ]);

  return {
    onUpdateToPolicyDetails,
    onCompleteQuote,
  };
};

export default usePolicyActions;
